'use client'

import { useState, useCallback } from 'react'
import { X, Plus, Archive, Maximize2, Minimize2 } from 'lucide-react'
import { Button } from '@/design-system'
import { MessageList } from './MessageList'
import { Composer } from './Composer'
import { ConversationSwitcher } from './ConversationSwitcher'
import Logo from '@/components/ui/Logo'
import { useAssistant } from './useAssistant'
import { useTranslations } from 'next-intl'
import type { Id } from '../../../convex/_generated/dataModel'

// Stable fallback so a missing conversation doesn't hand MessageList a fresh
// array identity on every render (which would defeat its memoization).
const EMPTY_MESSAGES: never[] = []

interface AssistantPanelProps {
  activeConversationId: Id<'assistantConversations'> | null
  isExpanded: boolean
  onToggleExpand: () => void
  onConversationChange: (id: Id<'assistantConversations'> | null) => void
  onClose: () => void
  /** Hide the expand/shrink button — used by the mobile full-screen chat
   *  where there is no smaller size to shrink back to. */
  hideExpandToggle?: boolean
}

export function AssistantPanel({
  activeConversationId,
  isExpanded,
  onToggleExpand,
  onConversationChange,
  onClose,
  hideExpandToggle = false,
}: AssistantPanelProps) {
  const t = useTranslations('assistant')

  // One-shot starter-prompt injection for the composer. Keystrokes are NOT
  // mirrored here — Composer owns its own text state so typing never
  // re-renders the panel (and with it, every chart in the message list).
  const [starterDraft, setStarterDraft] = useState('')

  const {
    conversations,
    conversationData,
    sendMessage,
    newConversation,
    cancelMessage,
    archiveConversation,
    retryMessage,
    isStreaming,
    streamingMessageId,
  } = useAssistant({ activeConversationId })

  // Select a conversation. "new" enters a draft state (activeConversationId =
  // null) WITHOUT writing to the DB — the conversation is created lazily on the
  // first send (see handleSend). This prevents empty "Untitled" conversations
  // from piling up when the user clicks + repeatedly.
  const handleConversationSelect = useCallback(
    (id: Id<'assistantConversations'> | 'new') => {
      onConversationChange(id === 'new' ? null : id)
    },
    [onConversationChange],
  )

  // Send a message — lazily creates the conversation if we're in a draft state.
  // conversationData === null means the active id is stale/inaccessible for the
  // current org (e.g. after an org switch or operator impersonation change), so
  // treat it as a draft and create a fresh conversation instead of sending to an
  // id that would be rejected with "conversation belongs to a different org".
  const handleSend = useCallback(
    async (content: string) => {
      let convId = activeConversationId
      if (!convId || conversationData === null) {
        convId = await newConversation()
        onConversationChange(convId)
      }
      await sendMessage(convId, content)
    },
    [
      activeConversationId,
      conversationData,
      sendMessage,
      newConversation,
      onConversationChange,
    ],
  )

  // Archive the active conversation, then drop back to a fresh draft state.
  const handleArchive = useCallback(async () => {
    if (!activeConversationId) return
    await archiveConversation(activeConversationId)
    onConversationChange(null)
  }, [activeConversationId, archiveConversation, onConversationChange])

  // Starter prompt: pre-fill the composer.
  const handleStarterPrompt = useCallback((prompt: string) => {
    setStarterDraft(prompt)
  }, [])

  const handleStarterConsumed = useCallback(() => {
    setStarterDraft('')
  }, [])

  // Stable identity so the memoized MessageList isn't re-rendered by a fresh
  // inline closure on every panel render.
  const handleRetry = useCallback(
    (messageId: string) => retryMessage(messageId as Id<'assistantMessages'>),
    [retryMessage],
  )

  const messages = conversationData?.messages ?? EMPTY_MESSAGES

  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--color-surface)' }}>
      {/* Gradient hairline bar — 3px, lime→cyan, pinned to top of panel */}
      <div
        aria-hidden="true"
        style={{
          height: '3px',
          flexShrink: 0,
          background: 'var(--gradient-lime-cyan)',
        }}
      />

      {/* Header */}
      <div
        className="flex items-center gap-1.5 px-3 py-2 flex-shrink-0 border-b"
        style={{ borderColor: 'var(--color-border)' }}
      >
        {/* Title */}
        <span style={{ fontSize: '0.95rem', fontWeight: 600, flexShrink: 0 }}>
          {t('panel.title')}{' '}
          <span
            style={{
              background: 'var(--gradient-lime-cyan)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t('panel.brandName')}
          </span>
        </span>

        {/* Conversation switcher — soft pill */}
        <div className="flex-1 min-w-0 ml-1">
          {conversations && conversations.length > 0 && (
            <ConversationSwitcher
              conversations={conversations}
              activeConversationId={activeConversationId}
              onSelect={handleConversationSelect}
            />
          )}
        </div>

        {/* Archive */}
        {activeConversationId != null && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleArchive}
            title={t('panel.archiveConversation')}
            className="flex-shrink-0 h-7 w-7"
          >
            <Archive className="h-3.5 w-3.5" />
          </Button>
        )}

        {/* New conversation */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => handleConversationSelect('new')}
          title={t('panel.newConversation')}
          className="flex-shrink-0 h-7 w-7"
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>

        {/* Expand / shrink */}
        {!hideExpandToggle && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleExpand}
            title={isExpanded ? t('panel.shrink') : t('panel.expand')}
            className="flex-shrink-0 h-7 w-7"
          >
            {isExpanded ? (
              <Minimize2 className="h-3.5 w-3.5" />
            ) : (
              <Maximize2 className="h-3.5 w-3.5" />
            )}
          </Button>
        )}

        {/* Close */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          title={t('panel.closeAssistant')}
          className="flex-shrink-0 h-7 w-7"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Message list */}
      <MessageList
        messages={messages as Parameters<typeof MessageList>[0]['messages']}
        onStarterPrompt={handleStarterPrompt}
        onRetry={handleRetry}
      />

      {/* Composer */}
      {activeConversationId != null || messages.length === 0 ? (
        <Composer
          isStreaming={isStreaming}
          streamingMessageId={streamingMessageId}
          starterDraft={starterDraft}
          onStarterConsumed={handleStarterConsumed}
          onSend={handleSend}
          onStop={cancelMessage}
        />
      ) : null}

      {/* Footer: keyboard hint left, "Powered by" + logo right */}
      <div className="flex justify-between items-center flex-shrink-0 px-3 pb-2 pt-0.5">
        <span
          style={{
            fontSize: '0.68rem',
            color: 'var(--color-text-muted)',
          }}
        >
          {t('panel.keyboardHint')}
        </span>
        <span className="flex items-center gap-1.5">
          <span
            style={{
              fontSize: '0.68rem',
              color: 'var(--color-text-muted)',
            }}
          >
            {t('panel.poweredBy')}
          </span>
          <Logo width={38} height={14} />
        </span>
      </div>
    </div>
  )
}
